import React from "react";
import { RfModal } from "./RfModal";
import { twClassNames } from "../../helpers/twClassNames";
import { IconFrame } from "../IconFrame";
import { ButtonType, ButtonProps } from "../Button";

export type ModalWithIconProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    type?: "info" | "danger" | "warning" | "success" | "dark";
    size?: "sm" | "md" | "lg" | "xl";
    icon?: string;
    showIcon?: boolean;
    title: string | React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    submitButtonText?: string | React.ReactNode;
    cancelButtonText?: string;
    showFooter?: boolean;
    submitButtonProps?: ButtonProps;
    cancelButtonProps?: ButtonProps;
    closeOnCancel?: boolean;
    closeOnSubmit?: boolean;
    modalClassName?: string;
    overlayClassName?: string;
    positionClassName?: string;
    panelClassName?: string;
    scrollInsideModal?: boolean;
    disableSubmitOnEnter?: boolean;
    titleClassName?: string;
    showCloseButton?: boolean;
    footerClassName?: string;
};

const styles = {
    info: "bg-tint text-brand",
    danger: "bg-pink-50 text-destructive",
    warning: "bg-yellow-100 text-yellow-600",
    success: "bg-green-50 text-success",
    dark: "bg-gray-800 text-white",
    sm: "max-w-lg", // same size as swal
    md: "max-w-xl",
    lg: "max-w-3xl",
    xl: "max-w-4xl"
};

export function ModalWithIcon({
    isOpen,
    onClose,
    onSubmit,
    onCancel,
    type = "info",
    size = "md",
    icon = "",
    title,
    children,
    className,
    submitButtonText,
    cancelButtonText,
    showFooter = true,
    submitButtonProps,
    cancelButtonProps,
    closeOnCancel = true,
    closeOnSubmit = false,
    modalClassName,
    overlayClassName,
    positionClassName,
    panelClassName,
    scrollInsideModal,
    disableSubmitOnEnter,
    titleClassName = "",
    showCloseButton = true,
    footerClassName = ""
}: ModalWithIconProps) {
    const iconColor = styles[type];
    const maxSize = styles[size];
    const submitButtonType =
        submitButtonProps?.type ||
        (type === "danger" ? "red" : type === "dark" ? "dark-secondary" : "primary");

    return (
        <RfModal
            isOpen={isOpen}
            onClose={onClose}
            className={modalClassName}
            disableSubmitOnEnter={disableSubmitOnEnter}
        >
            <RfModal.Overlay className={overlayClassName} />
            <RfModal.Panel
                className={twClassNames(
                    `${maxSize} gap-6`,
                    type === "dark" && "border border-gray-600 bg-gray-700 text-white",
                    panelClassName
                )}
                positionClassName={twClassNames(
                    positionClassName,
                    scrollInsideModal && "h-fit flex-col justify-start"
                )}
            >
                {showCloseButton && (
                    <RfModal.CloseButton
                        className={twClassNames(type === "dark" && "hover:bg-gray-600")}
                    />
                )}
                <div className={`${icon && "mt-2"} modalHeader flex flex-col gap-3`}>
                    {icon && (
                        <div className="flex justify-center">
                            <IconFrame
                                icon={icon}
                                size="lg"
                                isCircle={true}
                                customColor={iconColor}
                            />
                        </div>
                    )}
                    <span
                        className={twClassNames(
                            "modalTitle w-full text-center text-lg font-semibold text-gray-900",
                            type === "dark" && "text-white",
                            titleClassName
                        )}
                    >
                        {title}
                    </span>
                </div>

                <div
                    className={twClassNames(
                        "modalContents flex flex-col gap-4 text-base text-gray-700",
                        type === "dark" && "text-white",
                        className
                    )}
                >
                    {children}
                </div>

                {showFooter && (
                    <RfModal.Footer
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        closeOnCancel={closeOnCancel}
                        submitButtonText={submitButtonText}
                        cancelButtonText={cancelButtonText}
                        submitButtonType={submitButtonType as ButtonType}
                        submitButtonProps={submitButtonProps}
                        cancelButtonProps={{
                            type:
                                cancelButtonProps?.type ||
                                (type === "dark" ? "dark-secondary" : "secondary"),
                            ...cancelButtonProps
                        }}
                        isFullWidth={true}
                        closeOnSubmit={closeOnSubmit}
                        className={footerClassName}
                    />
                )}
            </RfModal.Panel>
        </RfModal>
    );
}
